import React, { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { BusyButton } from "../../components/etams/BusyButton";
import { toast } from "sonner";
import { catatLaporanAset, apiErrorMessage } from "../../lib/asetApi";
import { label } from "../../hooks/useAsetMeta";
import { periodeLabel } from "./bantuAset";

// Form cepat laporan bulanan (L3c, 4b-3) — satu dialog kecil per baris view
// `v_aset_laporan_bulanan_status`; dipakai dari layar Laporan Bulanan Aset
// dan tab Laporan Bulanan di Detail Aset.
//
// Cermin `aset_service.catat_laporan` + RPC `aset_catat_laporan_bulanan`:
//   * periode dari BARIS (YYYY-MM-01), nol diketik; pelapor = PIC aset (backend
//     memaksanya — ditampilkan sebagai keterangan "Dilaporkan atas nama").
//   * CHECK DB: BERPINDAH_LOKASI wajib `lokasi_baru`; catatan WAJIB bila
//     keberadaan ≠ ADA_DI_LOKASI ATAU kondisi ≠ BAIK (`aset_laporan_catatan_wajib_bila_nol_beres`);
//     kendaraan wajib km (KM_WAJIB_KENDARAAN). Layar menuntun; galat backend verbatim.
//   * HILANG_TIDAK_DITEMUKAN → konfirmasi dulu: status aset jadi Hilang dan
//     semua jadwal maintenance aset itu dinonaktifkan (keputusan (ii)/(iii), R4).
//   * Sesudah sukses toast menyebut akibat dari respons (status_aset_baru,
//     jadwal_dinonaktifkan).

const KOSONG = { status_keberadaan: "", kondisi_fisik: "", lokasi_baru: "", km: "", catatan: "" };

/**
 * @param baris  baris view: {aset_id, aset_kode, aset_nama, aset_kategori, periode, pic_employee_id, ...}
 * @param namaPic  nama PIC aset (dari daftar karyawan region), boleh null
 */
export default function LaporanFormDialog({ terbuka, onTutup, onSelesai, meta, baris, namaPic }) {
  const [form, setForm] = useState(KOSONG);
  const [konfirmasiHilang, setKonfirmasiHilang] = useState(false);
  const [menyimpan, setMenyimpan] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const kendaraan = baris?.aset_kategori === "KENDARAAN_BERMOTOR";
  const pindah = form.status_keberadaan === "BERPINDAH_LOKASI";
  const hilang = form.status_keberadaan === "HILANG_TIDAK_DITEMUKAN";
  const beres = form.status_keberadaan === "ADA_DI_LOKASI" && form.kondisi_fisik === "BAIK";

  useEffect(() => { if (terbuka) { setForm(KOSONG); setKonfirmasiHilang(false); } }, [terbuka, baris]);

  const validasi = () => {
    if (!form.status_keberadaan) { toast.error("Keberadaan aset wajib dipilih."); return false; }
    if (!form.kondisi_fisik) { toast.error("Kondisi fisik wajib dipilih."); return false; }
    if (pindah && !form.lokasi_baru.trim()) { toast.error("Lokasi baru wajib diisi bila aset pindah lokasi."); return false; }
    if (kendaraan && form.km === "") { toast.error("Km wajib diisi untuk kendaraan bermotor."); return false; }
    if (!beres && !form.catatan.trim()) { toast.error("Catatan wajib diisi bila aset tidak ada di lokasi atau kondisinya tidak baik."); return false; }
    return true;
  };

  const kirim = async () => {
    setMenyimpan(true);
    try {
      const body = {
        periode: String(baris.periode).slice(0, 10),
        status_keberadaan: form.status_keberadaan,
        kondisi_fisik: form.kondisi_fisik,
      };
      if (form.lokasi_baru.trim()) body.lokasi_baru = form.lokasi_baru.trim();
      if (form.catatan.trim()) body.catatan = form.catatan.trim();
      if (kendaraan && form.km !== "") body.km = Number(form.km);
      const r = await catatLaporanAset(baris.aset_id, body);
      let pesan = `Laporan ${baris.aset_kode} periode ${periodeLabel(baris.periode)} tersimpan.`;
      if (r.data?.status_aset_baru) pesan += ` Status aset: ${label(meta, "status_aset", r.data.status_aset_baru)}.`;
      if (r.data?.jadwal_dinonaktifkan) pesan += ` ${r.data.jadwal_dinonaktifkan} jadwal maintenance dinonaktifkan.`;
      toast.success(pesan);
      setKonfirmasiHilang(false);
      onSelesai(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan laporan."));
    } finally {
      setMenyimpan(false);
    }
  };

  const simpan = () => {
    if (!validasi()) return;
    if (hilang) { setKonfirmasiHilang(true); return; }
    kirim();
  };

  if (!baris) return null;
  return (
    <>
      <Dialog open={terbuka && !konfirmasiHilang} onOpenChange={(o) => { if (!o) onTutup(); }}>
        <DialogContent data-testid="laporan-form-dialog">
          <DialogHeader>
            <DialogTitle>Laporan Bulanan — {baris.aset_kode} {baris.aset_nama}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Periode <span className="text-foreground font-medium">{periodeLabel(baris.periode)}</span>
              {" · "}Dilaporkan atas nama: <span className="text-foreground">{namaPic || "PIC aset"}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Keberadaan *</Label>
                <Select value={form.status_keberadaan} onValueChange={(v) => upd("status_keberadaan", v)}>
                  <SelectTrigger data-testid="laporan-keberadaan"><SelectValue placeholder="Pilih keberadaan" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.status_keberadaan || []).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kondisi Fisik *</Label>
                <Select value={form.kondisi_fisik} onValueChange={(v) => upd("kondisi_fisik", v)}>
                  <SelectTrigger data-testid="laporan-kondisi"><SelectValue placeholder="Pilih kondisi" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.kondisi || []).map((k) => <SelectItem key={k.code} value={k.code}>{k.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {pindah && (
              <div>
                <Label>Lokasi Baru *</Label>
                <Input value={form.lokasi_baru} onChange={(e) => upd("lokasi_baru", e.target.value)} data-testid="laporan-lokasi-baru" />
              </div>
            )}
            {kendaraan && (
              <div>
                <Label>Km Saat Ini *</Label>
                <Input type="number" min="0" value={form.km} onChange={(e) => upd("km", e.target.value)} data-testid="laporan-km" />
              </div>
            )}
            <div>
              <Label>Catatan{beres || !form.status_keberadaan || !form.kondisi_fisik ? "" : " *"}</Label>
              <Textarea rows={2} value={form.catatan} onChange={(e) => upd("catatan", e.target.value)} data-testid="laporan-catatan" />
              {!beres && form.status_keberadaan && form.kondisi_fisik && (
                <div className="text-xs text-muted-foreground mt-1">Wajib diisi bila aset tidak ada di lokasi atau kondisinya tidak baik.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onTutup} disabled={menyimpan}>Batal</Button>
            <BusyButton onClick={simpan} busy={menyimpan} busyText="Menyimpan..." data-testid="laporan-simpan">Simpan</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={terbuka && konfirmasiHilang} onOpenChange={(o) => { if (!o) setKonfirmasiHilang(false); }}>
        <DialogContent data-testid="laporan-konfirmasi-hilang">
          <DialogHeader><DialogTitle>Laporkan Aset Hilang?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Aset <span className="font-mono text-xs">{baris.aset_kode}</span> <span className="font-medium text-foreground">{baris.aset_nama}</span> akan
            dicatat <span className="font-medium text-foreground">{label(meta, "status_keberadaan", "HILANG_TIDAK_DITEMUKAN")}</span>.
            Akibatnya: status aset berubah menjadi <span className="font-medium text-foreground">{label(meta, "status_aset", "HILANG")}</span> dan
            semua jadwal maintenance aset ini dinonaktifkan. Kalau aset ditemukan lagi, Kepala Cabang mengubah statusnya dari halaman Detail Aset.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKonfirmasiHilang(false)} disabled={menyimpan}>Kembali</Button>
            <BusyButton variant="destructive" onClick={kirim} busy={menyimpan} busyText="Menyimpan..." data-testid="laporan-konfirmasi-hilang-ya">Ya, laporkan hilang</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
